import { create } from 'zustand';
import { ToastVariant, type ToastItem } from '@/components/ui/toast/Toast.types';

const DEFAULT_DURATION_MS = 3500;

export interface ShowToastConfig {
  message: string;
  variant?: ToastVariant;
  durationMs?: number;
  action?: ToastItem['action'];
  icon?: ToastItem['icon'];
}

type ConvenienceOptions = Omit<ShowToastConfig, 'message' | 'variant'>;

interface ToastState {
  toasts: ToastItem[];
  /** Enqueue a toast; returns its id. */
  showToast: (config: ShowToastConfig) => string;
  showSuccess: (message: string, options?: ConvenienceOptions) => string;
  showInfo: (message: string, options?: ConvenienceOptions) => string;
  showError: (message: string, options?: ConvenienceOptions) => string;
  dismissToast: (id: string) => void;
}

let counter = 0;
const nextId = (): string => `toast-${++counter}`;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  showToast: ({ message, variant = ToastVariant.success, durationMs = DEFAULT_DURATION_MS, action, icon }) => {
    const id = nextId();
    set((state) => ({ toasts: [...state.toasts, { id, variant, message, durationMs, action, icon }] }));
    return id;
  },
  showSuccess: (message, options) =>
    get().showToast({ ...options, message, variant: ToastVariant.success }),
  showInfo: (message, options) =>
    get().showToast({ ...options, message, variant: ToastVariant.info }),
  showError: (message, options) =>
    get().showToast({ ...options, message, variant: ToastVariant.error }),
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));
